import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Fact } from '../../types';
import { triviaApi } from '../../services/api';

// 狀態類型
interface FactsState {
  dailyFact: Fact | null;
  recentFacts: Fact[];
  loading: boolean;
  error: string | null;
}

const initialState: FactsState = {
  dailyFact: null,
  recentFacts: [],
  loading: false,
  error: null,
};

// 異步 action: 獲取隨機冷知識
export const fetchRandomFact = createAsyncThunk(
  'facts/fetchRandomFact',
  async (userId?: string, rejectWithValue?: any) => {
    try {
      const fact = await triviaApi.getRandomTrivia(userId);
      return fact;
    } catch (error) {
      return rejectWithValue('無法獲取隨機冷知識');
    }
  }
);

// 異步 action: 獲取所有冷知識
export const fetchRecentFacts = createAsyncThunk(
  'facts/fetchRecentFacts',
  async (_, { rejectWithValue }) => {
    try {
      const facts = await triviaApi.getAllTrivia();
      return facts;
    } catch (error) {
      return rejectWithValue('無法獲取冷知識列表');
    }
  }
);

// 異步 action: 獲取多個隨機冷知識
export const fetchRandomFactsList = createAsyncThunk(
  'facts/fetchRandomList',
  async (
    { count, userId }: { count?: number; userId?: string },
    { rejectWithValue }
  ) => {
    try {
      const facts = await triviaApi.getRandomTriviaList(count, userId);
      return facts;
    } catch (error) {
      return rejectWithValue('無法獲取隨機冷知識列表');
    }
  }
);

const factsSlice = createSlice({
  name: 'facts',
  initialState,
  reducers: {
    likeFact: (state, action: PayloadAction<string>) => {
      const factId = action.payload;

      if (state.dailyFact && state.dailyFact.id === factId) {
        state.dailyFact.likes += 1;
      }

      const factIndex = state.recentFacts.findIndex(
        (fact) => fact.id === factId
      );
      if (factIndex !== -1) {
        state.recentFacts[factIndex].likes += 1;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // 處理獲取隨機冷知識
      .addCase(fetchRandomFact.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRandomFact.fulfilled, (state, action) => {
        state.loading = false;
        state.dailyFact = action.payload;
      })
      .addCase(fetchRandomFact.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // 處理獲取所有冷知識
      .addCase(fetchRecentFacts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecentFacts.fulfilled, (state, action) => {
        state.loading = false;
        state.recentFacts = action.payload;
      })
      .addCase(fetchRecentFacts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // 處理獲取多個隨機冷知識
      .addCase(fetchRandomFactsList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRandomFactsList.fulfilled, (state, action) => {
        state.loading = false;
        state.recentFacts = action.payload;
      })
      .addCase(fetchRandomFactsList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { likeFact } = factsSlice.actions;
export default factsSlice.reducer;
